import Lenis from 'lenis'
import { singleton, onCleanup } from './cleanup.js'

/**
 * Crea la instancia única de Lenis y arranca su bucle rAF.
 * Se ejecuta una sola vez por sesión: instanciar Lenis en cada transición
 * dejaba instancias huérfanas (con sus listeners sobre window) y un bucle
 * requestAnimationFrame por navegación corriendo en paralelo para siempre.
 * @returns {Lenis}
 */
function createLenis () {
  const lenis = new Lenis({
    smoothWheel: true,
    wheelMultiplier: 1,
    duration: 1,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothTouch: false, // Mobile
    touchMultiplier: 2, // sensibility on mobile
    infinite: false, // Infinite scrolling
    prevent: (node) => {
      // Excluir modal-box y sus descendientes
      if (node.classList.contains('modal-box') || node.closest('.modal-box')) {
        return true;
      }
      // Excluir contenedores con scroll interno (.opciones y elementos con overflow-y-auto)
      if (node.classList.contains('lenis-box') || node.closest('.lenis-box')) {
        return true;
      }
      return false;
    }
  })

  // Store the lenis instance on the window object for use in other modules
  window.lenisInstance = lenis

  const raf = (time) => {
    lenis.raf(time)
    requestAnimationFrame(raf)
  }

  requestAnimationFrame(raf)

  return lenis
}

/**
 * Inicializa (o revincula) el scroll suave.
 * La instancia es singleton; sólo los anchors, que viven dentro del
 * transition-wrapper y se destruyen en cada navegación, se revinculan.
 */
function lenisScroll () {
  const lenis = singleton('lenis', createLenis)
  const isFirstRun = !lenisScroll.initialized

  if (!isFirstRun) {
    // Tras una transición el documento cambió de alto y el scroll se reseteó
    // a 0 (transitions.js). Resincronizamos la instancia existente para
    // reproducir el estado que antes daba una instancia recién creada.
    lenis.resize()
    lenis.scrollTo(0, { immediate: true })
  }

  lenisScroll.initialized = true

  const smoothLinks = document.querySelectorAll('.anchor')

  smoothLinks.forEach(link => {
    const anchor = link.getAttribute('href')

    const onAnchorClick = (e) => {
      e.preventDefault()

      lenis.scrollTo(anchor, {
        duration: 2, // Puedes ajustar la duración según tus necesidades
        easing: (t) => 1 - Math.pow(1 - t, 6) // Ejemplo de una función de easing personalizada
      })
    }

    link.addEventListener('click', onAnchorClick)
    onCleanup(() => link.removeEventListener('click', onAnchorClick))
  })
}

lenisScroll.initialized = false

export default lenisScroll
